import React, { useState, useRef } from 'react'
import _ from 'lodash'
import { sharePermissionConstants } from '../../constants/sharePermissionConstants'
import InputBox from '../InputBox'
import { validateEmailId } from '../../../../server/src/routes/validator'
import TextBox from '../TextBox'
import Dropdown from '../Dropdown'
import {
  arraysEqualIgnoreOrder,
  renderFieldLabel,
  renderPermissionBoxHeader,
  renderSpinner,
  validateTextareaContent,
} from './helpers'
import { handleSharePermissionRequestActions, ACTION_TEXT } from './apiUtils'
import { apiErrorMessages } from './apiErrorMessages'

const RequestSharePermission = (props) => {
  const [shareWithUserMailId, setShareWithUserMailId] = useState('')
  const [shareWithUserList, setShareWithUserList] = useState(
    props?.requestDetailsFromServer?.sharee_email_addresses || [],
  )
  const [invalidShareeList, setInvalidShareeList] = useState([])
  const [
    shareeEmailAddressesErrorMsg,
    setShareeEmailAddressesErrorMsg,
  ] = useState('')
  const [businessJustification, setBusinessJustification] = useState(
    props?.requestDetailsFromServer?.business_justification || '',
  )
  const [
    businessJustificationErrorMsg,
    setBusinessJustificationErrorMsg,
  ] = useState('')
  const [dataSource, setDataSource] = useState(
    props?.requestDetailsFromServer?.data_source || '',
  )
  const [dataSourceErrorMsg, setDataSourceErrorMsg] = useState('')
  const [duration, setDuration] = useState(
    props?.requestDetailsFromServer?.duration_in_days || '',
  )
  const [durationErrorMsg, setDurationErrorMsg] = useState('')
  const [dataClassification, setDataClassification] = useState(
    props?.requestDetailsFromServer?.data_classification || '',
  )
  const [dataClassificationErrorMsg, setDataClassificationErrorMsg] = useState(
    '',
  )
  const [orgDetails, setOrgDetails] = useState(
    props?.requestDetailsFromServer?.infosec_domain || '',
  )
  const [orgDetailsErrorMsg, setOrgDetailsErrorMsg] = useState('')
  const [
    submitShareRequestInProgress,
    setSubmitShareRequestInProgress,
  ] = useState(false)
  const [statusMsg, setStatusMsg] = useState({
    msg: '',
    status: 'success',
    emailError: false,
  })

  const textInput = useRef(null)
  const textArea = useRef(null)

  const setStatusMessage = (msg, status, errorStatus = false) => {
    setStatusMsg({
      msg,
      status,
      emailError: errorStatus,
    })
  }

  const removeUser = (email) => {
    setStatusMessage('', 'success', false)
    setShareWithUserList(shareWithUserList.filter((item) => item !== email))
    setInvalidShareeList(invalidShareeList.filter((item) => item !== email))
  }

  const checkForSelfMailId = (newMailId, username, mailIdToCheck) => {
    return (
      newMailId.replace('@paypalcorp.com', '') === username ||
      newMailId.replace('@paypal.com', '') === username ||
      newMailId === mailIdToCheck
    )
  }

  const validateAndUpdateMailIdList = (inputMailId) => {
    const { username, email } = props.user

    const inputMailIdList = inputMailId
      .split(';')
      .map((id) => id.trim()) // remove whitespace
      .filter((id) => id) // remove empty strings

    // Remove duplicates already present in shareWithUserList
    const uniqueNewMailIds = inputMailIdList.filter(
      (id) => !shareWithUserList.includes(id),
    )

    if (uniqueNewMailIds.length === 0) {
      // No new IDs to process, just clear input
      setShareWithUserMailId('')
      return
    }

    // Separate self id if exists
    const [_selfIdList, validMailIdList] = _.partition(
      uniqueNewMailIds,
      (id) => {
        return checkForSelfMailId(id, username, email)
      },
    )

    // Get valid and invalid mail id list
    const [_validMailIds, invalidMailIds] = _.partition(
      validMailIdList,
      (id) => {
        return validateEmailId(id)
      },
    )

    // Filter out already existing invalid mail IDs
    const uniqueInvalidMailIds = invalidMailIds.filter(
      (id) => !invalidShareeList.includes(id),
    )

    setShareWithUserList([...shareWithUserList, ...validMailIdList])
    setInvalidShareeList([...invalidShareeList, ...uniqueInvalidMailIds])
    setShareWithUserMailId('')
  }

  const captureMailId = (evt) => {
    const inputMailId = evt.target.value
    if (
      (evt.key === 'Enter' || evt.type === 'blur') &&
      inputMailId.trim() !== ''
    ) {
      validateAndUpdateMailIdList(inputMailId)
    }
  }

  const onChangeMailId = (evt) => {
    if (
      shareWithUserList.length >=
      sharePermissionConstants.TotalNoOfShareePerRequest
    ) {
      setStatusMessage(
        sharePermissionConstants.Only10EmailIdsAllowedErrorMsg,
        'error',
        true,
      )
      return
    }
    setShareWithUserMailId(evt.target.value)
    setShareeEmailAddressesErrorMsg('')
    setStatusMessage('', 'success', false)
  }

  const editEmailId = (email) => {
    setShareWithUserMailId(email)
    removeUser(email)
    textInput.current.focus()
  }

  const handleDataSourceChange = (evt) => {
    const source = evt.target.value
    setDataSource(source)
    if (
      source.length >= sharePermissionConstants.fields.data_source.maxCharacters
    ) {
      setDataSourceErrorMsg(apiErrorMessages.INVALID_STRING_LENGTH.data_source)
      return
    }
    setDataSourceErrorMsg('')
    setStatusMessage('', 'success', false)

    if (evt.type === 'blur' && source.trim() !== '') {
      const sourceErrorMsg = validateTextareaContent(source, 'data_source')
      if (sourceErrorMsg !== '') {
        setDataSourceErrorMsg(sourceErrorMsg)
      }
    }
  }

  const handleBusinessJustificationChange = (evt) => {
    const justification = evt.target.value
    setBusinessJustification(justification)
    if (
      justification.length >=
      sharePermissionConstants.fields.business_justification.maxCharacters
    ) {
      setBusinessJustificationErrorMsg(
        apiErrorMessages.INVALID_STRING_LENGTH.business_justification,
      )
      return
    }
    setBusinessJustificationErrorMsg('')
    setStatusMessage('', 'success', false)

    if (evt.type === 'blur' && justification.trim() !== '') {
      const justificationErrorMsg = validateTextareaContent(
        justification,
        'business_justification',
      )
      if (justificationErrorMsg !== '') {
        setBusinessJustificationErrorMsg(justificationErrorMsg)
      }
    }
  }

  const setErrorMsgForFields = (errorResult) => {
    // Map each field to its setter
    const setterByField = {
      business_justification: setBusinessJustificationErrorMsg,
      data_source: setDataSourceErrorMsg,
      duration_in_days: setDurationErrorMsg,
      infosec_domain: setOrgDetailsErrorMsg,
      data_classification: setDataClassificationErrorMsg,
      sharee_email_addresses: setShareeEmailAddressesErrorMsg,
    }

    // Clear previous errors first
    Object.values(setterByField).forEach((set) => set(''))

    // Apply errors
    if (errorResult?.fieldBasedError) {
      errorResult.fieldBasedError.forEach(({ field, msg }) => {
        if (typeof setterByField[field] === 'function') {
          setterByField[field](msg)
        }
      })
    }
  }

  /* Identify fields changed for update */
  const identifyChangedFields = () => {
    const { requestDetailsFromServer = {} } = props || {}
    const changedFields = []

    // Field mappings (state → requestDetailsFromServer)
    const fieldMap = {
      business_justification: businessJustification,
      duration_in_days: duration,
      data_classification: dataClassification,
      infosec_domain: orgDetails,
      data_source: dataSource,
    }

    // Handle sharee_email_addresses separately since it's an array
    const shareWithUserListFromProps =
      requestDetailsFromServer.sharee_email_addresses || []
    if (
      !arraysEqualIgnoreOrder(shareWithUserList, shareWithUserListFromProps)
    ) {
      changedFields.push('sharee_email_addresses')
    }

    // Compare other fields
    Object.entries(fieldMap).forEach(([field, currentValue]) => {
      if (currentValue !== (requestDetailsFromServer[field] ?? '')) {
        changedFields.push(field)
      }
    })

    return changedFields
  }

  /* Build JSON-Patch replace ops from a list of changed field names present in `values`. */
  const buildJsonPatch = (changed, values) => {
    const valuesToUpdate = changed
      .map((field) =>
        field in values
          ? { op: 'replace', path: `/${field}`, value: values[field] }
          : null,
      )
      .filter(Boolean)
    const patchValues = { valuesToUpdate }
    return patchValues
  }

  /* Handle Share Permission Request */
  const requestPermissionAction = async (actionType) => {
    actionType = actionType === 'submit' ? 'create' : actionType

    // Single source of truth for current values
    const values = {
      sharee_email_addresses: shareWithUserList,
      business_justification: businessJustification,
      duration_in_days: Number(duration),
      data_classification: dataClassification,
      infosec_domain: orgDetails,
      data_source: dataSource,
    }

    // Guard for updates with no changes
    const changedFields = identifyChangedFields()
    if (actionType === 'update' && !changedFields?.length) {
      setStatusMessage(
        sharePermissionConstants.statusMsg.noChangesDetected,
        'info',
      )
      return
    }
    const path =
      actionType === 'create' ? '' : props?.requestDetailsFromServer?.request_id
    const params =
      actionType === 'create' ? values : buildJsonPatch(changedFields, values)

    try {
      setSubmitShareRequestInProgress(true)
      const result = await handleSharePermissionRequestActions(
        path,
        params,
        actionType,
      )
      if (result?.status === 'success') {
        props.setSubmitRequestSuccess(result?.data || {}, {
          msg: result.message,
          status: result.status,
        })
        return
      }

      setErrorMsgForFields(result)
      setStatusMessage(result.msg, result.status ?? 'error', true)
    } catch (err) {
      const msg = sharePermissionConstants.statusMsg.requestActionError.replace(
        '{operation}',
        ACTION_TEXT[actionType].prog,
      )
      setStatusMessage(msg, 'error')
      console.error(msg, err)
    } finally {
      setSubmitShareRequestInProgress(false)
    }
  }

  const handleDurationChange = (evt) => {
    const newDuration = evt.target.value
    setStatusMessage('', 'success', false)
    setDuration(newDuration)
    setDurationErrorMsg('')
    if (
      (evt.key === 'Enter' || evt.type === 'blur') &&
      newDuration.trim() !== ''
    ) {
      const durationValue = parseInt(newDuration, 10)
      if (isNaN(durationValue) || durationValue < 1 || durationValue > 365) {
        setDurationErrorMsg(
          sharePermissionConstants.fields.duration.invalidDuration,
        )
      } else {
        setDurationErrorMsg('')
      }
    }
  }

  const handleOrgDetailsChange = (index) => {
    const selectedValue =
      sharePermissionConstants.fields.org_details.options[index].value
    setOrgDetails(selectedValue)
    setOrgDetailsErrorMsg('')
    setStatusMessage('', 'success', false)
  }

  const handleDataClassificationChange = (index) => {
    const selectedValue =
      sharePermissionConstants.fields.data_classification.options[index].value
    setDataClassification(selectedValue)
    setDataClassificationErrorMsg('')
    setStatusMessage('', 'success', false)
  }

  const checkSubmitButtonVisibility = () => {
    return (
      shareWithUserList.length > 0 &&
      invalidShareeList.length === 0 &&
      businessJustification !== '' &&
      duration !== '' &&
      dataClassification !== '' &&
      orgDetails !== '' &&
      dataSource !== '' &&
      businessJustificationErrorMsg === '' &&
      durationErrorMsg === '' &&
      dataClassificationErrorMsg === '' &&
      orgDetailsErrorMsg === '' &&
      dataSourceErrorMsg === ''
    )
  }

  const renderPermissionRequestAction = () => {
    const { isEditRequest } = props
    const actionType = isEditRequest ? 'update' : 'create'
    let actions = sharePermissionConstants.requestedDetailsActions
    actions = actions.filter((action) => action.renderFor.includes(actionType))

    return (
      <div className="share__permission_action-container">
        {actions.map((action, index) => {
          return (
            <div className="share__action" key={index}>
              <button
                className={`${action.className} ${
                  (action.key === 'submit' || action.key === 'update') &&
                  !checkSubmitButtonVisibility()
                    ? 'disable__button'
                    : ''
                }`}
                disabled={
                  (action.key === 'submit' || action.key === 'update') &&
                  !checkSubmitButtonVisibility()
                }
                onClick={() =>
                  action.key === 'submit' || action.key === 'update'
                    ? requestPermissionAction(actionType)
                    : props.setSharePermissionBoxVisibility(false, {})
                }
              >
                <span className="uploadButton">{action.title}</span>
              </button>
            </div>
          )
        })}
      </div>
    )
  }

  const renderOrgDetails = () => {
    const orgDetailsConstant = sharePermissionConstants.fields.org_details
    return (
      <div className="share__permission-data-classification">
        <div className="share__permission-data-classification-content">
          {renderFieldLabel(
            orgDetailsConstant.title,
            '',
            orgDetailsConstant.isMandatory,
          )}
          <div className="share__permission-data-classification-content_container">
            <Dropdown
              label={orgDetailsConstant.placeholder}
              className="share_permission_data_classification_dropdown"
              options={orgDetailsConstant.options}
              value={orgDetails}
              handleOnSelect={handleOrgDetailsChange}
              style={{}}
              errorText={orgDetailsErrorMsg}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderDataClassification = () => {
    const dataClassificationConstant =
      sharePermissionConstants.fields.data_classification
    const infoContent = `${dataClassificationConstant.note.info} <a href="${dataClassificationConstant.note.link}" target="_blank" rel="noreferrer">${dataClassificationConstant.note.linkText}</a>`
    return (
      <div className="share__permission-data-classification">
        <div className="share__permission-data-classification-content">
          {renderFieldLabel(
            dataClassificationConstant.title,
            infoContent,
            dataClassificationConstant.isMandatory,
            true,
          )}
          <div className="share__permission-data-classification-content_container">
            <Dropdown
              label={dataClassificationConstant.placeholder}
              className="share_permission_data_classification_dropdown"
              options={dataClassificationConstant.options}
              value={dataClassification}
              handleOnSelect={handleDataClassificationChange}
              style={{}}
              errorText={dataClassificationErrorMsg}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderDuration = () => {
    const durationConstant = sharePermissionConstants.fields.duration
    return (
      <div className="share__permission-duration">
        <div className="share__permission-duration-content">
          {renderFieldLabel(
            durationConstant.title,
            durationConstant.note.info,
            durationConstant.isMandatory,
          )}
          <div className="share__permission-recipient-content_container">
            <InputBox
              name={durationConstant.key}
              id={durationConstant.key}
              value={duration}
              label={durationConstant.label}
              className={durationConstant.className}
              isLabelHidden={durationConstant.isLabelHidden}
              placeholder={durationConstant.placeholder}
              onChangeText={(e) => handleDurationChange(e)}
              onKeyPressText={(e) => handleDurationChange(e)}
              onBlurText={(e) => handleDurationChange(e)}
              autoFocus={false}
              errorText={durationErrorMsg}
              maxLength={durationConstant.maxLength}
              type={durationConstant.type}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderDataSource = () => {
    const dataSourceConstant = sharePermissionConstants.fields.data_source
    return (
      <div className="share__permission-justification">
        <div className="share__permission-justification-content">
          {renderFieldLabel(
            dataSourceConstant.title,
            '',
            dataSourceConstant.isMandatory,
          )}
          <div className="share__permission-justification-content_container">
            <TextBox
              ref={textArea}
              name={dataSourceConstant.key}
              id={dataSourceConstant.key}
              className={dataSourceConstant.className}
              placeholder={dataSourceConstant.placeholder}
              maxCharacters={dataSourceConstant.maxCharacters}
              value={dataSource}
              onChangeText={(evt) => handleDataSourceChange(evt)}
              onBlurText={(evt) => handleDataSourceChange(evt)}
              errorText={dataSourceErrorMsg}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderBusinessJustification = () => {
    const justificationConstant =
      sharePermissionConstants.fields.business_justification
    return (
      <div className="share__permission-justification">
        <div className="share__permission-justification-content">
          {renderFieldLabel(
            justificationConstant.title,
            '',
            justificationConstant.isMandatory,
          )}
          <div className="share__permission-justification-content_container">
            <TextBox
              ref={textArea}
              name={justificationConstant.key}
              id={justificationConstant.key}
              className={justificationConstant.className}
              placeholder={justificationConstant.placeholder}
              maxCharacters={justificationConstant.maxCharacters}
              value={businessJustification}
              onChangeText={(evt) => handleBusinessJustificationChange(evt)}
              onBlurText={(evt) => handleBusinessJustificationChange(evt)}
              errorText={businessJustificationErrorMsg}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderSelectedShareRecipients = () => {
    return (
      <div>
        {shareWithUserList.length > 0 &&
          shareWithUserList.map((item, index) => {
            return (
              <div
                className={`share__permission-recipients-container ${
                  invalidShareeList.includes(item)
                    ? 'share__permission-recipient_error-id'
                    : 'share__permission-recipient_valid-id'
                }`}
                key={`add_user_${index}`}
              >
                <div className="dFlex">
                  <div
                    className="share__permission-recipient-label"
                    data-test-id={`share__permission-recipient_${item}_${
                      invalidShareeList.includes(item) ? 'invalid' : 'valid'
                    }`}
                    onClick={() => editEmailId(item)}
                  >
                    <div className="dFlex">
                      <div className="tooltip share__permission-recipient-id">
                        {item}
                        {invalidShareeList.includes(item) && (
                          <span
                            className="tooltiptext"
                            style={{ color: 'red' }}
                          >
                            {sharePermissionConstants.InvalidEmailId}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    className="share__permission-recipient-clear"
                    data-test-id={`share__permission-recipient_${item}_remove`}
                    onClick={() => removeUser(item)}
                  >
                    <span
                      className={`ppvx_icon ppvx_icon--size_sm ppvx_icon-clear ${
                        invalidShareeList.includes(item)
                          ? 'share__permission-recipient_error-icon'
                          : ''
                      }`}
                    />
                  </button>
                </div>
              </div>
            )
          })}
      </div>
    )
  }

  const renderShareRecipientDetails = () => {
    const recipientConstant = sharePermissionConstants.fields.share_recipient
    return (
      <div className="share__permission-recipient">
        <div className="share__permission-recipient-content">
          {renderFieldLabel(
            recipientConstant.title,
            '',
            recipientConstant.isMandatory,
          )}
          <div className="share__permission-recipient-content_container">
            <InputBox
              name={recipientConstant.key}
              ref={textInput}
              id={recipientConstant.key}
              value={shareWithUserMailId}
              label={recipientConstant.label}
              className={recipientConstant.className}
              isLabelHidden={recipientConstant.isLabelHidden}
              placeholder={recipientConstant.placeholder}
              onChangeText={(e) => onChangeMailId(e)}
              onKeyPressText={(e) => captureMailId(e)}
              onBlurText={(e) => captureMailId(e)}
              autoFocus={false}
              type={recipientConstant.type}
              errorText={shareeEmailAddressesErrorMsg}
            />
          </div>
          {renderSelectedShareRecipients()}
        </div>
      </div>
    )
  }

  const renderstatusMsg = () => {
    return (
      <div
        className={`ppvx_alert ppvx_alert--type_${statusMsg.status} permission__request_alert-box`}
        data-test-id={`permission__request_alert_${statusMsg.status}`}
        role="alert"
      >
        {' '}
        {statusMsg.msg}
      </div>
    )
  }

  const renderCreateRequestForm = () => {
    return (
      <div
        className="ppvx_modal-backdrop"
        id="permission_request_form"
        role="dialog"
        aria-hidden={props.showPermissionBox ? 'false' : 'true'}
        data-ppvx_modal-background-close="true"
      >
        <article className="ppvx_modal" tabIndex="-1">
          {renderPermissionBoxHeader(
            sharePermissionConstants.Title,
            props.setSharePermissionBoxVisibility,
          )}
          <section className="share__permission-section">
            {statusMsg.msg !== '' && (
              <div className="errorMsg_box">{renderstatusMsg()}</div>
            )}
            {renderShareRecipientDetails()}
            {renderBusinessJustification()}
            {renderDataSource()}
            {renderDuration()}
            <div className="share__permission_data-org-holder">
              {renderDataClassification()}
              {renderOrgDetails()}
            </div>
          </section>
          {renderPermissionRequestAction()}
          {submitShareRequestInProgress && renderSpinner()}
        </article>
      </div>
    )
  }

  return <>{renderCreateRequestForm()}</>
}
export default RequestSharePermission
